import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { SingleValue } from "chakra-react-select";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../../components/fields/SelectField/ChakraReactSelectField";
import { DateTimeField } from "../../components/DateTimeField/DateTimeField";
import { ApiEnumConfigurations } from "../../enums/ApiEnumConfigurations";
import { useApiRequest } from "../../hooks/useApiRequest";
import { NotificationFormValues } from "./types";
import { setTimezones } from "./NotificationForm";
import { CollapsibleSection } from "./CollapsibleSection";
import { AdvancedSettings } from "./AdvancedSettings";
import { CheckCircleFillIcon, WarningFillIcon } from "../../styles/icons";
import DateUtil from "../../util/DateUtil";
import { isEmptyStr } from "../../util/StringUtil";

interface NotificationScheduleProps {
  businessId: string;
  isOpen: boolean;
  onToggle: () => void;
}

const DEFAULT_TIMEZONE_KEY = "AUS Eastern Standard Time";

const SCHEDULE_FIELD_KEYS = [
  "sendDateTime",
  "timeZoneId",
  "endDateTime",
  "stopSendingOption",
] as const;

export const NotificationSchedule: React.FC<NotificationScheduleProps> = ({
  businessId,
  isOpen,
  onToggle,
}) => {
  const { values, errors, setFieldValue } =
    useFormikContext<NotificationFormValues>();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const { data: timezones, isLoading } = useApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        businessId,
        ApiEnumConfigurations.Timezone
      ),
    []
  );

  // Pass timezones to the form for validation
  useEffect(() => {
    if (timezones) {
      setTimezones(timezones);
    }
  }, [timezones]);

  // Set default timezone to Sydney if no timezone is set
  useEffect(() => {
    if (timezones && !values.timeZoneId) {
      const sydneyTimezone = timezones.find(
        (tz) => tz.Key === DEFAULT_TIMEZONE_KEY
      );
      if (sydneyTimezone) {
        setFieldValue("timeZoneId", sydneyTimezone.Key);
      }
    }
  }, [timezones, values.timeZoneId, setFieldValue]);

  // Some timezones can be behind the current date, so set minDate to 2 days ago to be safe
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - 2);

  const locationOverrideCount = Object.values(
    values.locationMessageOverrides ?? {}
  ).filter((v) => !isEmptyStr(v)).length;

  const hasScheduleFieldError = SCHEDULE_FIELD_KEYS.some(
    (key) => !!errors[key]
  );
  const isScheduleIncomplete =
    !values.sendDateTime ||
    isEmptyStr(values.timeZoneId) ||
    (locationOverrideCount > 0 && !values.endDateTime);
  const showErrorBadge = hasScheduleFieldError || isScheduleIncomplete;

  const rightAdornment = showErrorBadge ? (
    <HStack
      bg="riskRating.reviewRequired.bg"
      borderRadius="full"
      px={3}
      py={1}
      spacing={2}
    >
      <WarningFillIcon color="riskRating.reviewRequired.icon" boxSize={4} />
      <Text fontSize="sm" color="riskRating.reviewRequired.icon">
        Date & Time Required
      </Text>
    </HStack>
  ) : (
    <HStack
      bg="riskRating.safe.bg"
      borderRadius="full"
      px={3}
      py={1}
      spacing={2}
    >
      <CheckCircleFillIcon color="riskRating.safe.icon" boxSize={4} />
      <Text fontSize="sm" color="riskRating.safe.icon">
        {DateUtil.getFormattedScheduleDateTime(values.sendDateTime)}
      </Text>
    </HStack>
  );

  return (
    <CollapsibleSection
      title="Notification Schedule"
      rightAdornment={rightAdornment}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <VStack w="100%" alignItems="flex-start" spacing={6} pb={10}>
        <ChakraReactSelectField
          label="Timezone"
          name="timeZoneId"
          selectedOptionColor="blue"
          isLoading={isLoading}
          options={
            timezones?.map((timezone) => {
              return {
                label: timezone.Value!,
                value: timezone.Key!,
              };
            }) || []
          }
          className="relative-listbox"
          onChange={(e) => {
            const value = (e as SingleValue<SelectOption>)?.value;
            setFieldValue("timeZoneId", value);
          }}
        />

        <VStack w="100%" alignItems="stretch" spacing={2}>
          <DateTimeField
            name="sendDateTime"
            label="Send Date & Time"
            minDate={minDate}
          />
          {locationOverrideCount > 0 && (
            <Text fontSize="sm" color="gray.600">
              Applies to both push notifications and location messaging.
            </Text>
          )}
        </VStack>

        {locationOverrideCount > 0 && (
          <VStack w="100%" alignItems="stretch" spacing={2}>
            <DateTimeField
              name="endDateTime"
              label="End Date & Time"
              minDate={minDate}
            />
            <Text fontSize="sm" color="gray.600" pt={1} pb={2}>
              Campaign message will revert back to their default location
              message at this time.
            </Text>
            {!values.endDateTime && (
              <Box
                bg="riskRating.reviewRequired.bg"
                border="1px solid"
                borderColor="riskRating.reviewRequired.border"
                borderRadius="md"
                p={3}
                mt={1}
              >
                <HStack spacing={3} alignItems="flex-start">
                  <WarningFillIcon
                    color="riskRating.reviewRequired.icon"
                    boxSize={5}
                    mt={0.5}
                  />
                  <VStack alignItems="flex-start" spacing={1}>
                    <Text fontSize="sm" fontWeight="700" color="gray.700">
                      Campaign end date is required
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      {locationOverrideCount} location(s) have customised
                      messages. You must set an end date when location messages
                      are overridden.
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}
          </VStack>
        )}
        <Box w="100%">
          <AdvancedSettings
            isOpen={isAdvancedOpen}
            onToggle={() => setIsAdvancedOpen((prev) => !prev)}
          />
        </Box>
      </VStack>
    </CollapsibleSection>
  );
};
